/**
 * @file usePlanValidation.js
 * @description Validation state hook for the plan workspace.
 *
 * Owns:
 *   ep 5  GET  /api/v1/plans/{id}/validate   — most recent persisted validation result (on mount)
 *   ep 4  POST /api/v1/plans/{id}/validate   — run full semantic validation on demand
 *
 * Supplies to PlanWorkspace:
 *   validationResult — shape consumed by PlanWorkspace + SubmissionReadinessPanel
 *   decisionContext  — shape consumed by SubmissionReadinessPanel
 *   runValidation()  — triggers POST ep 4 and updates state
 *
 * validationResult shape (consumed by PlanWorkspace lines 330-341):
 *   hasBlockingErrors: boolean
 *   blockingCount:     number
 *   warningCount:      number
 *   errors:            object (field → message map for section components)
 *   groups:            validationGroups[] (for SubmissionReadinessPanel)
 *   validationStatus:  string|null
 *   validatedAt:       string|null
 *
 * decisionContext shape (consumed by SubmissionReadinessPanel):
 *   quotaFreshness:       { status, freshnessAt, affectedPositionCount }
 *   creditFreshness:      { status, freshnessAt }
 *   populationReadiness:  { status, resolvedCount, totalScopeCount, blockingIssueCount }
 *   projectionAvailable:  boolean
 *   eligibleApproverExists: boolean
 *
 * Spec refs:
 *   api_spec_fileA_v4_final.md §1.4 — POST /api/v1/plans/{id}/validate
 *   api_spec_fileA_v4_final.md §1.5 — GET  /api/v1/plans/{id}/validate
 *
 * Decision #14 (spec §0): SAVE = structural integrity. SUBMIT = full semantic validation.
 * This hook owns the SUBMIT-time validation only. Field-level structural
 * validation is local to each section component.
 *
 * @author data-fit LLC · DecisionFlow · S807
 * @since S807
 */

import { useCallback, useEffect, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// Normalise validation response
// Spec §1.4 response shape:
// {
//   planId, validationStatus, blockingIssueCount, warningCount,
//   validationGroups: [{ groupId, groupName, status, issues: [{ issueCode, severity, message, field, sectionId }] }],
//   validatedAt,
//   decisionContext: { quotaFreshness, creditFreshness, populationReadiness, projectionAvailable, eligibleApproverExists }
// }
// ---------------------------------------------------------------------------

/**
 * Build SubmissionReadinessPanel.validationResult shape from spec ep 4/5 response.
 * Panel expects: { canSubmit, blocking[], warnings[], passed[], noEligibleApprover, noEligibleApproverDetail }
 * Spec returns:  { validationStatus, blockingIssueCount, warningCount, validationGroups[], decisionContext }
 */
function buildPanelValidationResult(raw, dc) {
  const blockingCount = raw?.blockingIssueCount ?? raw?.blocking_issue_count ?? 0;
  const groups        = raw?.validationGroups   ?? raw?.validation_groups    ?? [];
  const eligibleApproverExists = dc?.eligibleApproverExists ?? dc?.eligible_approver_exists ?? true;

  // Flatten validationGroups → blocking / warnings / passed arrays
  const blocking = [];
  const warnings = [];
  const passed   = [];

  groups.forEach((group) => {
    (group.issues ?? []).forEach((issue, idx) => {
      const item = {
        id:         issue.issueCode ?? `${group.groupId}-${idx}`,
        severity:   issue.severity === 'blocking' ? 'blocking' : issue.severity === 'warning' ? 'warning' : 'passed',
        message:    issue.message ?? '',
        sectionKey: issue.sectionId ?? null,
        fieldKey:   issue.field     ?? null,
      };
      if (item.severity === 'blocking') blocking.push(item);
      else if (item.severity === 'warning') warnings.push(item);
      else passed.push(item);
    });
  });

  // canSubmit: no blocking issues AND eligible approver exists
  const canSubmit = blockingCount === 0 && eligibleApproverExists;

  return {
    canSubmit,
    blocking,
    warnings,
    passed,
    noEligibleApprover:       !eligibleApproverExists,
    noEligibleApproverDetail: eligibleApproverExists ? null : 'No CA-2 approver with scope covering this plan was found.',
  };
}

/**
 * Build SubmissionReadinessPanel.decisionContext shape from spec ep 4/5 decisionContext.
 * Panel expects: { freshnessSignals[], populationCount, projectionNote }
 */
function buildPanelDecisionContext(dc) {
  if (!dc) {
    return {
      freshnessSignals: [],
      populationCount: 'Not assessed',
      projectionNote: null,
    };
  }

  const freshnessSignals = [];

  const qf = dc.quotaFreshness ?? dc.quota_freshness ?? null;
  if (qf) {
    freshnessSignals.push({
      signal:      'quota',
      status:      qf.status === 'ready' ? 'current' : qf.status === 'stale' ? 'stale' : 'warning',
      freshnessAt: qf.freshnessAt ?? new Date().toISOString(),
      label:       'Quota data',
    });
  }

  const cf = dc.creditFreshness ?? dc.credit_freshness ?? null;
  if (cf) {
    freshnessSignals.push({
      signal:      'credits',
      status:      cf.status === 'ready' ? 'current' : cf.status === 'stale' ? 'stale' : 'warning',
      freshnessAt: cf.freshnessAt ?? new Date().toISOString(),
      label:       'Credit data',
    });
  }

  const pr = dc.populationReadiness ?? dc.population_readiness ?? null;
  const resolvedCount   = pr?.resolvedCount   ?? pr?.resolved_count   ?? 0;
  const totalScopeCount = pr?.totalScopeCount ?? pr?.total_scope_count ?? 0;
  const popStatus = pr?.status === 'ready' ? 'current' : pr?.status === 'partial' ? 'warning' : 'stale';

  freshnessSignals.push({
    signal:      'population',
    status:      popStatus,
    freshnessAt: new Date().toISOString(),
    label:       'Matching population',
  });

  const projectionAvailable = dc.projectionAvailable ?? dc.projection_available ?? false;

  return {
    freshnessSignals,
    populationCount: `${resolvedCount} of ${totalScopeCount} positions resolved`,
    projectionNote: projectionAvailable ? null : 'Projected payout not yet available for this plan.',
  };
}

function normalizeValidation(raw) {
  if (!raw) {
    const emptyDc = buildPanelDecisionContext(null);
    return {
      // Spec shape (for PlanWorkspace validationState)
      validationResult: {
        hasBlockingErrors: false,
        blockingCount:     0,
        warningCount:      0,
        errors:            {},
        groups:            [],
        validationStatus:  null,
        validatedAt:       null,
      },
      // Panel shape (for SubmissionReadinessPanel)
      panelValidationResult: {
        canSubmit: false,
        blocking: [], warnings: [], passed: [],
        noEligibleApprover: false, noEligibleApproverDetail: null,
      },
      decisionContext: {
        quotaFreshness:       { status: 'missing', freshnessAt: null, affectedPositionCount: 0 },
        creditFreshness:      { status: 'missing', freshnessAt: null },
        populationReadiness:  { status: 'not_assessed', resolvedCount: 0, totalScopeCount: 0, blockingIssueCount: 0 },
        projectionAvailable:  false,
        eligibleApproverExists: false,
      },
      panelDecisionContext: emptyDc,
    };
  }

  const blockingCount = raw.blockingIssueCount ?? raw.blocking_issue_count ?? 0;
  const warningCount  = raw.warningCount       ?? raw.warning_count        ?? 0;
  const groups        = raw.validationGroups   ?? raw.validation_groups    ?? [];
  const dc            = raw.decisionContext    ?? raw.decision_context     ?? {};

  const errors = {};
  groups.forEach((group) => {
    (group.issues ?? []).forEach((issue) => {
      if (issue.severity === 'blocking' && issue.field) {
        errors[issue.field] = issue.message;
      }
    });
  });

  return {
    validationResult: {
      hasBlockingErrors: blockingCount > 0,
      blockingCount,
      warningCount,
      errors,
      groups,
      validationStatus: raw.validationStatus ?? raw.validation_status ?? null,
      validatedAt:      raw.validatedAt      ?? raw.validated_at      ?? null,
    },
    panelValidationResult: buildPanelValidationResult(raw, dc),
    decisionContext: {
      quotaFreshness:       dc.quotaFreshness  ?? dc.quota_freshness  ?? { status: 'missing', freshnessAt: null, affectedPositionCount: 0 },
      creditFreshness:      dc.creditFreshness ?? dc.credit_freshness ?? { status: 'missing', freshnessAt: null },
      populationReadiness:  dc.populationReadiness ?? dc.population_readiness ?? { status: 'not_assessed', resolvedCount: 0, totalScopeCount: 0, blockingIssueCount: 0 },
      projectionAvailable:  dc.projectionAvailable  ?? dc.projection_available  ?? false,
      eligibleApproverExists: dc.eligibleApproverExists ?? dc.eligible_approver_exists ?? false,
    },
    panelDecisionContext: buildPanelDecisionContext(dc),
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * @param {number} planId
 * @returns {{
 *   validationResult: object,
 *   decisionContext: object,
 *   runValidation: () => Promise<void>,
 *   validating: boolean,
 *   error: string|null
 * }}
 */
export default function usePlanValidation(planId) {
  const [normalized, setNormalized] = useState(() => normalizeValidation(null));
  const [validating, setValidating] = useState(false);
  const [error,      setError]      = useState(null);

  const mountedRef = useRef(true);
  const abortRef   = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // ---------------------------------------------------------------------------
  // Load most recent persisted result on mount (ep 5 GET), then immediately
  // refresh via POST (ep 4) to ensure the header badge reflects current state.
  //
  // OI-VALIDATE-CACHE-STALE S1022:
  // GET /validate returns the cached data_readiness_status row — it may reflect
  // a stale validation run from a previous session. Without a POST refresh on
  // mount, the header badge can show an incorrect blocking issue count until the
  // user explicitly clicks Submit. The GET result is applied first (fast, avoids
  // a blank badge) then immediately superseded by the POST result (authoritative).
  // AD-S1021-VALIDATE-POST-BEFORE-SUBMIT: POST /validate runs fresh validation
  // and updates cache. GET /validate returns cached result only.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!planId) return;

    const controller = new AbortController();
    abortRef.current = controller;

    const loadAndRefresh = async () => {
      // Step 1: GET — populate badge quickly with last known state
      try {
        const getRes = await fetch(`/api/v1/plans/${planId}/validate`, {
          credentials: 'include',
          signal: controller.signal,
        });
        if (getRes.ok && !controller.signal.aborted && mountedRef.current) {
          const data = await getRes.json();
          if (!controller.signal.aborted && mountedRef.current) {
            setNormalized(normalizeValidation(data));
          }
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        // Non-fatal: validation state starts empty if GET fails
      }

      // Step 2: POST — immediately refresh with authoritative current state
      // so the header badge is never driven by a stale cached result.
      if (controller.signal.aborted || !mountedRef.current) return;
      try {
        const postRes = await fetch(`/api/v1/plans/${planId}/validate`, {
          method:      'POST',
          credentials: 'include',
          signal:      controller.signal,
          headers:     { 'Content-Type': 'application/json' },
          body:        JSON.stringify({ validationContext: 'on_demand' }),
        });
        if (postRes.ok && !controller.signal.aborted && mountedRef.current) {
          const data = await postRes.json();
          if (!controller.signal.aborted && mountedRef.current) {
            setNormalized(normalizeValidation(data));
          }
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        // Non-fatal: keep GET result if POST fails
      }
    };

    loadAndRefresh();

    return () => { controller.abort(); };
  }, [planId]);

  // ---------------------------------------------------------------------------
  // Run validation on demand (ep 4 POST)
  // Spec §1.4: { validationContext: 'pre_submit' | 'on_demand' }
  // Decision #14: SUBMIT = full semantic validation — called from handleSubmitRequest
  // ---------------------------------------------------------------------------
  const runValidation = useCallback(async (context = 'pre_submit', overridePlanId = null) => {
    // S818: overridePlanId lets caller pass V_new planId at call time during amendment mode.
    const targetPlanId = overridePlanId ?? planId;
    if (!targetPlanId || validating) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setValidating(true);
    setError(null);

    try {
      const response = await fetch(`/api/v1/plans/${targetPlanId}/validate`, {
        method:      'POST',
        credentials: 'include',
        signal:      controller.signal,
        headers:     { 'Content-Type': 'application/json' },
        body:        JSON.stringify({ validationContext: context }),
      });

      if (!mountedRef.current || controller.signal.aborted) return;

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(
          errBody.detail || `Validation failed (${response.status}).`
        );
      }

      const data = await response.json();

      if (!mountedRef.current || controller.signal.aborted) return;

      setNormalized(normalizeValidation(data));

    } catch (err) {
      if (!mountedRef.current || err.name === 'AbortError') return;
      setError(err.message || 'Validation failed.');
    } finally {
      if (mountedRef.current && !controller.signal.aborted) {
        setValidating(false);
      }
    }
  }, [planId, validating]);

  return {
    // Spec shape — consumed by PlanWorkspace validationState (hasBlockingErrors, blockingCount, warningCount)
    validationResult: normalized.validationResult,
    // Panel shape — consumed by SubmissionReadinessPanel directly
    panelValidationResult: normalized.panelValidationResult,
    decisionContext:       normalized.decisionContext,
    panelDecisionContext:  normalized.panelDecisionContext,
    runValidation,
    validating,
    error,
  };
}
